import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const HOVER_COMMIT_DELAY_MS = 150;

/**
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 * @property {HTMLElement | null} activeOverflowItem
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu]
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {State} */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onMenuClick, true);
    document.addEventListener('click', this.#onOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onMenuClick, true);
    document.removeEventListener('click', this.#onOutsideClick);

    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Toggle only when the menu control itself is clicked.
   * Links inside the submenu keep their normal behavior.
   * @param {MouseEvent} event
   */
  #onMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem || !this.contains(listItem)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const disclosure = this.#expandableFor(item);
    const clickedControl =
      item.contains(event.target) ||
      disclosure.contains(event.target);

    if (!clickedControl) return;

    const hasSubmenu =
      Boolean(findSubmenu(item)) || listItem.slot === 'more';

    if (!hasSubmenu) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#toggleItem(item);
  };

  /**
   * Close when clicking outside this menu.
   * @param {MouseEvent} event
   */
  #onOutsideClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Close on Escape and restore focus.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const item = this.#state.activeItem;
    if (!item) return;

    event.preventDefault();

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Keep compatibility with existing disclosure bindings.
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#toggleItem(item);
  };

  /**
   * @param {HTMLElement} item
   */
  #toggleItem(item) {
    const disclosure = this.#expandableFor(item);
    const isExpanded =
      disclosure.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.#deactivate(this.#state.activeItem ?? item, {
        force: true,
      });

      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== disclosure) focusTarget.focus();
    } else {
      this.#activateItem(item);
    }
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';

    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector(
        '[slot="more"] [ref="menuitem"]'
      );

      if (moreButton instanceof HTMLElement) return moreButton;
    }

    return this.#expandableFor(item);
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector(
      '[ref="disclosure[]"]'
    );

    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      )
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector(
      '[slot="overflow"] [ref="menuitem"]'
    );

    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  /**
   * Ignore hover and focus activation from existing bindings.
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (event.type !== 'click') return;
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#activateItem(item);
  };

  /**
   * Open the selected menu using the existing theme layout.
   * @param {HTMLElement} item
   */
  #activateItem(item) {
    if (!this.headerComponent) return;
    if (item === this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger
      ? this.#getFirstOverflowMenuItem()
      : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem =
      this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';

      const previousSubmenu = findSubmenu(previouslyActiveItem);

      if (previousSubmenu) {
        previousSubmenu.inert = true;
        delete previousSubmenu.dataset.active;
      }
    }

    if (
      previouslyActiveOverflowItem &&
      previouslyActiveOverflowItem !== previouslyActiveItem
    ) {
      this.#expandableFor(
        previouslyActiveOverflowItem
      ).ariaExpanded = 'false';

      const previousOverflowSubmenu = findSubmenu(
        previouslyActiveOverflowItem
      );

      if (previousOverflowSubmenu) {
        previousOverflowSubmenu.inert = true;
        delete previousOverflowSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;

    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';

    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';

      const activeOverflowSubmenu = findSubmenu(overflowItem);

      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert = false;
        activeOverflowSubmenu.dataset.active = '';
      }
    }

    const overflowItemSubmenu = isMoreTrigger
      ? findSubmenu(overflowItem)
      : null;

    let submenu =
      findSubmenu(item) ||
      (overflowItemSubmenu ? this.overflowMenu : null);

    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#cleanupMutationObserver();

    if (submenu) {
      const committedItem = item;

      this.#hoverDispatchTimer = setTimeout(() => {
        this.#hoverDispatchTimer = undefined;

        if (this.#state.activeItem === committedItem) {
          this.dispatchEvent(new MegaMenuHoverEvent());
        }
      }, HOVER_COMMIT_DELAY_MS);

      submenu.dataset.active = '';
      submenu.inert = false;

      const observedSubmenu = submenu;

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.#state.activeItem !== committedItem) return;

            if (observedSubmenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                '--submenu-height',
                `${observedSubmenu.offsetHeight}px`
              );

              this.#cleanupMutationObserver();
            }
          });
        });
      });

      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      const currentObserver = this.#submenuMutationObserver;

      setTimeout(() => {
        if (this.#submenuMutationObserver === currentObserver) {
          this.#cleanupMutationObserver();
        }
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Existing hover and blur bindings must not close the menu.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // Closing is handled by clicks and Escape.
  }

  /**
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (
    item = this.#state.activeItem,
    { force = false } = {}
  ) => {
    if (!force) return;
    if (!item || item !== this.#state.activeItem) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.#cleanupMutationObserver();

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu =
      findSubmenu(item) ||
      (item.closest('[slot="more"]') ? this.overflowMenu : null);

    const activeOverflowItem = this.#state.activeOverflowItem;

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;

    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';

    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';

      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);

      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert = true;
        delete activeOverflowSubmenu.dataset.active;
      }
    }

    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector('slot')
      ?.assignedElements();

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (
          link.querySelector('[ref="submenu[]"]')
        );

        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  /**
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute(
      'data-submenu-overlap-bottom-row'
    );

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? (
          /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector('.header__row--top')
          )?.offsetHeight ?? 0
        )
      : this.headerComponent.offsetHeight;
  }

  /**
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const fullOpenHeaderHeight =
      submenuHeight === 0
        ? 0
        : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem =
    element.closest('.menu-list__list-item') ?? element;

  const item = listItem.querySelector('[ref="menuitem"]');

  return item instanceof HTMLElement ? item : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');

  const submenu = listItem?.querySelector(
    ':scope > [ref="submenu[]"]'
  );

  return submenu instanceof HTMLElement ? submenu : null;
}
